import API_BASE_URL from '../config/api';

/**
 * Submit a step of the unit form to the backend
 * 
 * Backend Endpoint: POST /api/field/units/steps
 * 
 * Request Body:
 * {
 *   "step": number,           // Step number (1-5)
 *   "unitId": string | null,  // Unit ID if updating existing submission (null for new)
 *   "buildingId": string,     // Building ID
 *   "maintenanceID": string,  // Maintenance ID (optional)
 *   "values": object          // Step-specific form values
 * }
 * 
 * Response:
 * {
 *   "success": boolean,
 *   "unitId": string,         // Unit ID for tracking this submission
 *   "message": string
 * }
 * 
 * @param {number} step - Step number (1-5)
 * @param {string|null} unitId - Unit ID if updating existing submission
 * @param {string} buildingId - Building ID
 * @param {string} maintenanceID - Maintenance ID (optional)
 * @param {object} values - Step-specific form values
 * @returns {Promise<object>} Response with unitId
 */
export async function submitStep(step, unitId, buildingId, maintenanceID, values) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/field/units/steps`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        step,
        unitId: unitId || null,
        buildingId,
        maintenanceID: maintenanceID || null,
        values,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Failed to submit step ${step}: ${response.status}`);
    }

    const data = await response.json();
    console.log(`[fieldApi] Step ${step} submitted successfully:`, data);
    return data;
  } catch (error) {
    console.error(`[fieldApi] Error submitting step ${step}:`, error);
    throw error;
  }
}

/**
 * Get existing unit submission data
 * 
 * Backend Endpoint: GET /api/field/units/:unitId
 * 
 * Response:
 * {
 *   "unitId": string,
 *   "buildingId": string,
 *   "maintenanceID": string,
 *   "step1": object,
 *   "step2": object,
 *   "step3": object,
 *   "step4": object,
 *   "createdAt": string,
 *   "updatedAt": string
 * }
 * 
 * @param {string} unitId - Unit ID
 * @returns {Promise<object>} Unit submission data
 */
export async function getUnitSubmission(unitId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/field/units/${unitId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Failed to get unit submission: ${response.status}`);
    }

    const data = await response.json();
    console.log('[fieldApi] Unit submission retrieved:', data);
    return data;
  } catch (error) {
    console.error('[fieldApi] Error getting unit submission:', error);
    throw error;
  }
}

